import { ChoicesArticle, ChoicesComponentFilter } from "@generated"
import { Row } from "@screens/configurator-v2/elements"
import { useVirtualizer } from "@tanstack/react-virtual"
import React, { useEffect, useRef } from "react"
import NextSlide from "./NextSlide"
import PrevSlide from "./PrevSlide"
import Slide from "./Slide"
import { useConfiguratorDimensions } from "./hooks/use-configurator-dimensions"
export type Variant = "desktop" | "mobile"

interface Props {
  activeIndex: number
  onClickPrev: () => void
  onClickNext: () => void
  onClickItem: (index: number) => void
  items: ChoicesArticle[]
  canMovePrev: boolean
  canMoveNext: boolean
  tab: ChoicesComponentFilter
}

export const DesktopCarousel: React.FC<Props> = ({
  activeIndex,
  onClickNext,
  onClickPrev,
  onClickItem,
  items,
  canMovePrev,
  canMoveNext,
  tab,
}) => {
  const ref = useRef()
  const { slideWidth, width, slidesBothSides } = useConfiguratorDimensions()
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    horizontal: true,
    getScrollElement: () => ref.current,
    estimateSize: () => slideWidth,
    paddingStart: slideWidth * slidesBothSides + slideWidth,
    paddingEnd: slideWidth * slidesBothSides + slideWidth,
    overscan: 20,
  })

  // Sync active index with virtualizer
  useEffect(() => {
    rowVirtualizer.scrollToIndex(activeIndex, {
      align: "center",
    })
  }, [activeIndex, rowVirtualizer, tab])

  useEffect(() => {
    rowVirtualizer.measure()
  }, [width, rowVirtualizer])

  return (
    <>
      <Row
        width={width}
        style={{
          overflow: "hidden",
          position: "relative",
        }}
        ref={ref}
      >
        <div
          style={{
            position: "relative",
            scrollBehavior: "smooth",
            height: 200,
            width: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((column) => {
            const isActive = column.key === activeIndex
            const item = items[column.index]

            return (
              <div
                key={column.key}
                ref={rowVirtualizer.measureElement}
                data-index={column.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  opacity: isActive ? 0 : 1,
                  width: isActive ? `${slideWidth * 2}px` : `${slideWidth}px`,
                  transform: `translateX(${column.start - slideWidth}px)`,
                }}
              >
                {item && (
                  <Slide
                    id={item.id}
                    name={item.name}
                    onClick={() => onClickItem(column.index)}
                  />
                )}
              </div>
            )
          })}
        </div>
      </Row>
      {canMovePrev && <PrevSlide onSelect={onClickPrev} />}
      {canMoveNext && <NextSlide onSelect={onClickNext} />}
    </>
  )
}
